/**
 * wallet/models.ts — types for Module #2, the Roamer Wallet™.
 *
 * A platform MODULE, not a core engine, and NOT a banking application: a
 * financial intelligence and planning companion. Integrates with Roamer Core™
 * only (bus events in, notifications out); rates and transactions arrive via
 * bus payloads, manual entry, or future provider integrations — never
 * fabricated.
 */

// ─── Spending categories ───────────────────────────────────────────────────────

export const SPENDING_CATEGORIES = [
  'housing',
  'food',
  'transportation',
  'coworking',
  'entertainment',
  'travel',
  'healthcare',
  'utilities',
  'shopping',
  'businessExpenses',
  'taxes',
  'emergency',
] as const;
export type SpendingCategory = (typeof SPENDING_CATEGORIES)[number];

// ─── Transactions ──────────────────────────────────────────────────────────────

export type TransactionKind = 'income' | 'expense';

export interface WalletTransaction {
  id: string;
  kind: TransactionKind;
  /** Amount in the transaction's own currency — never silently converted. */
  amount: number;
  currency: string;
  category: SpendingCategory | null; // null for income
  description: string;
  city: string | null;
  at: number;
  /** How the record entered the wallet. */
  source: 'bus' | 'manual' | 'provider';
}

// ─── Currency ──────────────────────────────────────────────────────────────────

export interface ExchangeRateEntry {
  /** 1 unit of `from` = `rate` units of `to`. */
  from: string;
  to: string;
  rate: number;
  at: number;
  source: 'manual' | 'provider';
}

export type ExchangeTrend = 'improving' | 'worsening' | 'stable' | 'unknown';

// ─── Wallet profile ────────────────────────────────────────────────────────────

export type BudgetHealth = 'healthy' | 'warning' | 'over-budget' | 'unknown';

export interface WalletProfile {
  homeCurrency: string;
  currentCurrency: string;
  preferredCurrencies: string[];
  monthlyIncomeGoal: number | null;
  monthlySpendingGoal: number | null;
  savingsGoal: number | null;
  emergencyFundGoal: number | null;
  /** Derived from real transactions; null until data exists. */
  averageMonthlyIncome: number | null;
  averageMonthlyExpenses: number | null;
  /** Latest home↔current rate; null until one is known. */
  currentExchangeRate: number | null;
  budgetHealth: BudgetHealth;
}

// ─── Goals ─────────────────────────────────────────────────────────────────────

export const GOAL_TYPES = [
  'income',
  'savings',
  'emergencyFund',
  'travel',
  'housing',
  'missionIncome',
] as const;
export type GoalType = (typeof GOAL_TYPES)[number];

export interface WalletGoal {
  id: string;
  type: GoalType;
  title: string;
  /** Target in home currency. */
  targetAmount: number;
  /** Derived automatically from transactions; manual adjustments allowed. */
  currentAmount: number;
  createdAt: number;
  reachedAt: number | null;
}

// ─── Budget engine ─────────────────────────────────────────────────────────────

export interface BudgetSnapshot {
  currency: string;
  dailyBudget: number | null;
  weeklyBudget: number | null;
  monthlyBudget: number | null;
  /** Remaining this calendar month. */
  remainingBudget: number | null;
  /** Straight-line projection from month-to-date pace. */
  projectedSpending: number | null;
  /** (income − expenses) / income for the current month. */
  savingsRate: number | null;
  /** Months of average expenses covered by recorded net savings. */
  runwayMonths: number | null;
  /** Transactions skipped because no exchange rate was known. */
  unconvertibleCount: number;
  health: BudgetHealth;
}

export interface CityCost {
  city: string;
  totalSpent: number;
  days: number;
  perDay: number | null;
}

// ─── Insights ──────────────────────────────────────────────────────────────────

export type InsightSeverity = 'positive' | 'info' | 'warning';

export interface WalletInsight {
  id: string;
  severity: InsightSeverity;
  /** Plain-language, actionable message. */
  message: string;
  /** Which real data produced it — insights are always evidence-based. */
  basis: string;
}

// ─── Reports ───────────────────────────────────────────────────────────────────

export interface PeriodSummary {
  label: string;
  currency: string;
  income: number;
  expenses: number;
  net: number;
  byCategory: Partial<Record<SpendingCategory, number>>;
  transactionCount: number;
  unconvertibleCount: number;
}

export interface TrendPoint {
  label: string;
  value: number;
}

export interface WalletReports {
  monthlySummary: PeriodSummary | null;
  yearlySummary: PeriodSummary | null;
  citySpendingComparison: CityCost[];
  incomeTrend: TrendPoint[];
  expenseTrend: TrendPoint[];
  currencyTrend: TrendPoint[];
  savingsGrowth: TrendPoint[];
}

// ─── Privacy / export ──────────────────────────────────────────────────────────

export interface WalletPrivacy {
  /** Financial data is private by default; nothing is shared unless enabled. */
  shareSummaries: boolean;
  shareGoals: boolean;
}

export const DEFAULT_WALLET_PRIVACY: WalletPrivacy = {
  shareSummaries: false,
  shareGoals: false,
};

export interface WalletExport {
  exportedAt: number;
  profile: WalletProfile;
  transactions: WalletTransaction[];
  rates: ExchangeRateEntry[];
  goals: WalletGoal[];
  privacy: WalletPrivacy;
}
